/**
 * usePayment Hook
 * 
 * Provides a clean API for triggering payments from any component.
 * Handles all pre-payment checks (auth, verification, geocomply)
 * before opening the payment modal.
 * 
 * Usage:
 * ```tsx
 * const { openPayment } = usePayment();
 * 
 * await openPayment(
 *   { id: 'pkg-1', title: 'Gold Pack', price: 9.99 },
 *   { onSuccess: () => console.log('Purchased!') }
 * );
 * ```
 */

import { useCallback, useSyncExternalStore } from 'react';
import { paymentStore, PaymentItem, PaymentCallbacks } from '@/stores/paymentStore';
import { useAuth } from '@/contexts/AuthContext';
import { useBalance } from '@/contexts/BalanceContext';
import { useVerification } from '@/contexts/VerificationContext';
import { useGeoComplyTiming } from '@/hooks/useGeoComplyTiming';
import { usePurchaseLimitsQuery } from '@/hooks/queries/useSiteConfigQuery';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import { purchaseLimitModalStore } from '@/stores/purchaseLimitModalStore';

export type OpenPaymentOptions = {
  /** Skip verification check (use with caution) */
  skipVerification?: boolean;
  /** Skip GeoComply check (use with caution) */
  skipGeoComply?: boolean;
  /** Close a modal before opening payment (e.g., close welcome modal) */
  closeModal?: () => void;
};

export function usePayment() {
  const { user } = useAuth();
  const { balance } = useBalance();
  const { checkVerificationAndPrompt } = useVerification();
  const { isInitialized, performCheck } = useGeoComplyTiming();
  const { data: purchaseLimits } = usePurchaseLimitsQuery();

  // Subscribe to isProcessing for UI feedback (optional usage)
  // This only causes re-render when isProcessing changes
  const isProcessing = useSyncExternalStore(
    paymentStore.subscribe,
    () => paymentStore.getState().isProcessing
  );

  // Subscribe to isOpen for components that need to know modal state
  const isPaymentModalOpen = useSyncExternalStore(
    paymentStore.subscribe,
    () => paymentStore.getState().isOpen
  );

  /**
   * Open the payment modal with pre-payment checks
   * 
   * @param item - The item to purchase (package, offer, etc.)
   * @param callbacks - Success/error/cancel callbacks
   * @param options - Optional configuration (skip checks, close modal)
   * @returns true if payment modal opened, false if blocked by checks
   */
  const openPayment = useCallback(
    async (
      item: PaymentItem,
      callbacks: PaymentCallbacks = {},
      options: OpenPaymentOptions = {}
    ): Promise<boolean> => {
      // Check 1: User must be logged in
      if (!user) {
        toast.error('Please log in to make a purchase');
        return false;
      }

      // Check 2: SC balance must be below configured maximum
      if (purchaseLimits?.max_sc_balance != null && balance) {
        const totalSc = (balance.sc_bonus ?? 0) + (balance.sc_purchased ?? 0) + (balance.sc_redeemable ?? 0);
        if (totalSc >= purchaseLimits.max_sc_balance) {
          toast.error(
            `Purchases are unavailable while your SC balance is ${purchaseLimits.max_sc_balance} SC or above. Please play or redeem (if applicable) your SC before buying more Gold Coins.`
          );
          return false;
        }
      }

      // Check 3: User must be verified (unless skipped)
      if (!options.skipVerification) {
        if (!checkVerificationAndPrompt('data')) {
          // Close the current modal so verification modal can show
          options.closeModal?.();
          return false;
        }
      }

      // Check 4: GeoComply location check (unless skipped)
      if (!options.skipGeoComply && isInitialized) {
        const isCompliant = await performCheck('make a purchase');
        if (!isCompliant) {
          return false;
        }
      }

      // Check 5: Responsible gaming purchase limit
      const amount = typeof item.price === 'number' ? item.price : parseFloat(String(item.price));
      if (!Number.isNaN(amount) && amount > 0) {
        try {
          const limitCheck = await apiClient.get<{ allowed: boolean; reason?: string }>(
            `/profile/purchase-limit-check?amount=${encodeURIComponent(amount)}`
          );
          if (limitCheck?.allowed === false) {
            purchaseLimitModalStore.open(
              limitCheck.reason || 'You have set a purchase limit. This purchase would exceed your limit.'
            );
            return false;
          }
        } catch {
          // Purchase limit check failed, allow purchase to proceed
        }
      }

      // All checks passed - open payment modal
      
      // Close any modal that was open before (e.g., welcome offer modal)
      options.closeModal?.();
      
      paymentStore.openPayment(item, callbacks);
      return true;
    },
    [user, balance, purchaseLimits, checkVerificationAndPrompt, isInitialized, performCheck]
  );

  /**
   * Close the payment modal (programmatic close)
   */
  const closePayment = useCallback(() => {
    paymentStore.closePayment();
  }, []);

  return {
    openPayment,
    closePayment,
    isProcessing,
    isPaymentModalOpen,
  };
}
